import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getProfile, updateProfile } from '../../services/apiRoutes';
import { toast } from 'react-toastify';
import { SaveIcon, PhotographIcon, UserIcon, ChartBarIcon, HomeIcon } from '@heroicons/react/outline';
import cx from 'classnames';

export default function CMSProfilePage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('umum');
  
  const [formData, setFormData] = useState({
    ProfilDesa: {
      nama: '',
      sambutan: '',
      url_gambar: '',
      luas_wilayah: '',
      luas_area_pertanian: '',
      luas_permukiman: '',
      latitude: '',
      longitude: ''
    },
    KepalaDesa: {
      nama: '',
      jabatan_obj: { nama: 'Keuchik' },
      telepon: '',
      email: '',
      url_gambar: ''
    },
    JumlahPenduduk: '0'
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getProfile(slug);
        if (!res.error) {
          const data = res.data.data;
          setFormData({
            ProfilDesa: {
              nama: data.ProfilDesa?.nama || '',
              sambutan: data.ProfilDesa?.sambutan || '',
              url_gambar: data.ProfilDesa?.url_gambar || '',
              luas_wilayah: data.ProfilDesa?.luas_wilayah || '0',
              luas_area_pertanian: data.ProfilDesa?.luas_area_pertanian || '0',
              luas_permukiman: data.ProfilDesa?.luas_permukiman || '0',
              latitude: data.ProfilDesa?.latitude || '',
              longitude: data.ProfilDesa?.longitude || ''
            },
            KepalaDesa: {
              nama: data.KepalaDesa?.nama || '',
              jabatan_obj: { nama: data.KepalaDesa?.jabatan_obj?.nama || 'Keuchik' },
              telepon: data.KepalaDesa?.telepon || '',
              email: data.KepalaDesa?.email || '',
              url_gambar: data.KepalaDesa?.url_gambar || ''
            },
            JumlahPenduduk: data.JumlahPenduduk || '0'
          });
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        toast.error("Gagal memuat profil");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const handleInputChange = (section, field, value) => {
    // Ensure value is never undefined
    const safeValue = value === undefined ? '' : value;

    if (section === 'JumlahPenduduk') {
      setFormData(prev => ({ ...prev, JumlahPenduduk: safeValue }));
    } else if (field && field.includes('.')) {
      const [f1, f2] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [f1]: { ...(prev[section]?.[f1] || {}), [f2]: safeValue }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: safeValue }
      }));
    }
  };

  const handleImageUpload = (section, field, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Warn if image is larger than 500KB (will cause Firestore document size issues)
    if (file.size > 500 * 1024) {
      toast.warn(`Gambar terlalu besar (${(file.size / 1024).toFixed(0)}KB). Disarankan < 500KB untuk menghindari error saat menyimpan.`, { autoClose: 6000 });
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      handleInputChange(section, field, base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await updateProfile(slug, formData);
      if (!res.error) {
        toast.success("Profil berhasil disimpan!");
      } else {
        // Check if this is an adblock network error
        if (res.message?.toLowerCase().includes('blocked') || res.message?.toLowerCase().includes('network')) {
          toast.error("Koneksi diblokir. Pastikan AdBlocker/uBlock dinonaktifkan untuk halaman ini, lalu coba lagi.", { autoClose: 8000 });
        } else {
          toast.error(res.message);
        }
      }
    } catch (error) {
      toast.error("Terjadi kesalahan saat menyimpan: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'umum', name: 'Profil Umum', icon: HomeIcon },
    { id: 'kepala', name: 'Kepala Desa', icon: UserIcon },
    { id: 'statistik', name: 'Statistik', icon: ChartBarIcon }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Manajemen Profil Gampong</h1>
          <p className="text-sm text-gray-500">Sesuaikan informasi yang tampil di halaman beranda</p>
        </div>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center px-6 py-2.5 bg-primary text-white rounded-xl font-bold shadow-lg shadow-primary/20 hover:bg-primary-dark transition-all disabled:opacity-50"
        >
          {saving ? 'Menyimpan...' : (
            <>
              <SaveIcon className="w-5 h-5 mr-2" />
              Simpan Perubahan
            </>
          )}
        </button>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cx(
                "flex items-center px-8 py-5 text-sm font-bold transition-all border-b-2 whitespace-nowrap",
                activeTab === tab.id 
                  ? "border-primary text-primary bg-primary/5" 
                  : "border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-50"
              )}
            >
              <tab.icon className="w-5 h-5 mr-3" />
              {tab.name}
            </button>
          ))}
        </div>

        <div className="p-8">
          {activeTab === 'umum' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Nama Gampong</label>
                  <input
                    type="text"
                    value={formData.ProfilDesa.nama}
                    onChange={(e) => handleInputChange('ProfilDesa', 'nama', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-100 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 font-medium"
                    placeholder="Masukkan nama gampong"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 px-1">Sambutan Kepala Desa</label>
                  <textarea
                    rows={4}
                    value={formData.ProfilDesa.sambutan}
                    onChange={(e) => handleInputChange('ProfilDesa', 'sambutan', e.target.value)}
                    className="w-full px-5 py-4 border-2 border-gray-100 bg-white rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary font-medium text-gray-600 transition-all duration-300 min-h-[120px]"
                    placeholder="Kalimat sambutan hangat untuk pengunjung..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Latitude</label>
                    <input
                      type="text"
                      value={formData.ProfilDesa.latitude}
                      onChange={(e) => handleInputChange('ProfilDesa', 'latitude', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-100 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 font-medium"
                      placeholder="Contoh: 5.12345"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Longitude</label>
                    <input
                      type="text"
                      value={formData.ProfilDesa.longitude}
                      onChange={(e) => handleInputChange('ProfilDesa', 'longitude', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-100 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 font-medium"
                      placeholder="Contoh: 97.12345"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 text-center uppercase tracking-wide">Gambar Hero (Background Beranda)</label>
                  <div className="relative group overflow-hidden rounded-3xl border-2 border-dashed border-gray-200 aspect-video flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    {formData.ProfilDesa.url_gambar ? (
                      <>
                        <img src={formData.ProfilDesa.url_gambar} alt="Hero" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                          <label className="cursor-pointer bg-white px-4 py-2 rounded-xl text-sm font-bold shadow-lg">Ganti Foto</label>
                        </div>
                      </>
                    ) : (
                      <div className="text-center p-8">
                        <PhotographIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Klik untuk upload background</p>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload('ProfilDesa', 'url_gambar', e)}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'kepala' && (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="w-full md:w-1/3">
                  <label className="block text-sm font-bold text-gray-700 mb-2">Foto Kepala Desa</label>
                  <div className="relative group overflow-hidden rounded-3xl border-2 border-dashed border-gray-200 aspect-[3/4] flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    {formData.KepalaDesa.url_gambar ? (
                      <>
                        <img src={formData.KepalaDesa.url_gambar} alt="Kepala Desa" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                          <label className="cursor-pointer bg-white px-4 py-2 rounded-xl text-sm font-bold shadow-lg">Ganti Foto</label>
                        </div>
                      </>
                    ) : (
                      <div className="text-center p-8">
                        <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Upload Foto</p>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload('KepalaDesa', 'url_gambar', e)}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </div>
                </div>
                <div className="w-full md:w-2/3 space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 px-1">Nama Lengkap</label>
                    <input
                      type="text"
                      value={formData.KepalaDesa.nama}
                      onChange={(e) => handleInputChange('KepalaDesa', 'nama', e.target.value)}
                      className="w-full px-5 py-3.5 border-2 border-gray-100 bg-white rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary font-bold text-gray-700 transition-all duration-300"
                      placeholder="Nama beserta gelar"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 px-1">Jabatan</label>
                    <input
                      type="text"
                      value={formData.KepalaDesa.jabatan_obj.nama}
                      onChange={(e) => handleInputChange('KepalaDesa', 'jabatan_obj.nama', e.target.value)}
                      className="w-full px-5 py-3.5 border-2 border-gray-100 bg-white rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary font-bold text-gray-700 transition-all duration-300"
                      placeholder="Contoh: Keuchik"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 px-1">Nomor HP / WhatsApp</label>
                    <input
                      type="text"
                      value={formData.KepalaDesa.telepon}
                      onChange={(e) => handleInputChange('KepalaDesa', 'telepon', e.target.value)}
                      className="w-full px-5 py-3.5 border-2 border-gray-100 bg-white rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary font-bold text-gray-700 transition-all duration-300"
                      placeholder="0812..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 px-1">Email</label>
                    <input
                      type="email"
                      value={formData.KepalaDesa.email}
                      onChange={(e) => handleInputChange('KepalaDesa', 'email', e.target.value)}
                      className="w-full px-5 py-3.5 border-2 border-gray-100 bg-white rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary font-bold text-gray-700 transition-all duration-300"
                      placeholder="email@gampong.id"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'statistik' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 px-1">Jumlah Penduduk</label>
                  <input
                    type="text"
                    value={formData.JumlahPenduduk}
                    onChange={(e) => handleInputChange('JumlahPenduduk', null, e.target.value)}
                    className="w-full px-6 py-4 border-2 border-gray-100 bg-white rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary font-black text-3xl text-primary transition-all duration-300 shadow-sm"
                    placeholder="Misal: 1,240"
                  />
                  <p className="text-[10px] text-gray-400 mt-2 italic px-2 font-medium">* Tampil di bagian Gampong dalam angka</p>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 px-1">Luas Wilayah (Ha)</label>
                  <input
                    type="text"
                    value={formData.ProfilDesa.luas_wilayah}
                    onChange={(e) => handleInputChange('ProfilDesa', 'luas_wilayah', e.target.value)}
                    className="w-full px-6 py-4 border-2 border-gray-100 bg-white rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary font-black text-2xl text-gray-700 transition-all duration-300 shadow-sm"
                    placeholder="Luas total wilayah"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 px-1">Area Pertanian (Ha)</label>
                  <input
                    type="text"
                    value={formData.ProfilDesa.luas_area_pertanian}
                    onChange={(e) => handleInputChange('ProfilDesa', 'luas_area_pertanian', e.target.value)}
                    className="w-full px-6 py-4 border-2 border-gray-100 bg-white rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary font-black text-2xl text-gray-700 transition-all duration-300 shadow-sm"
                    placeholder="Luas area tani"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 px-1">Permukiman (Ha)</label>
                  <input
                    type="text"
                    value={formData.ProfilDesa.luas_permukiman}
                    onChange={(e) => handleInputChange('ProfilDesa', 'luas_permukiman', e.target.value)}
                    className="w-full px-6 py-4 border-2 border-gray-100 bg-white rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary font-black text-2xl text-gray-700 transition-all duration-300 shadow-sm"
                    placeholder="Luas area rumah"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
